import CloseIcon from '@mui/icons-material/Close';
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';
import { Box, IconButton, List, Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { csrfTokenCall, IRecruiterTeamNameAutoCompleteOption } from 'wisestep-api';
import {
  WsButton,
  WsColors,
  WsTypography,
  WsTypoVariants,
  BuildingIcon,
  UserRoundIcon,
  WsTypoColors,
  BriefcaseBusinessIcon,
} from 'wisestep-base';

import { ClientAutocomplete } from '../../../../../components/ClientAutocomplete';
import { ClientRecruiterAutocomplete } from '../../../../../components/ClientRecruiterAutocomplete';
import {
  CreatedBwDatesFilter,
  JobTypeFilter,
  LocationsFilter,
  NoticePeriod,
  RecruitersFilter,
  SalaryRangeFilter,
  StageStatusFilter,
  TagsFilter,
  TeamFilter,
  WsAccordion,
} from '../../../../../components/filters/components';
import BusinessUnitFilter from '../../../../../components/filters/components/BusinessUnitFilter';
import JobTitleFilterNew from '../../../../../components/filters/components/JobTitleFilterNew';
import RelocationPreferenceFilter from '../../../../../components/filters/components/RelocationPreferenceFilter';
import { ISubmissionAdvanceFilters, ITokens } from '../../../utils/Helper';

export interface ISubmissionsAdvancedFiltersProps {
  onClose: () => void;
  onSubmit: (fields: ISubmissionAdvanceFilters) => void;
  defaultValues: ISubmissionAdvanceFilters;
}

const AdvancedFilters = ({
  onClose,
  onSubmit,
  defaultValues,
}: ISubmissionsAdvancedFiltersProps) => {
  const methods = useForm<ISubmissionAdvanceFilters>({
    defaultValues,
  });

  const { handleSubmit, reset, watch, getValues } = methods;
  const [tokens, setTokens] = useState<ITokens>({ auth: '', token: '' });

  const [resetFlag, setResetFlag] = useState<boolean>(false);
  const [resetKey, setResetKey] = useState(0);
  useEffect(() => {
    const getToken = async () => {
      const res = await csrfTokenCall();
      const token = res.config.headers?.['X-XSRF-TOKEN'];
      const auth = res.config.headers?.['Authorization'];
      setTokens({ auth: auth ?? '', token: token ?? '' });
    };
    getToken();
  }, []);

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
      setResetKey((prev) => prev + 1);
    }
  }, [defaultValues, reset]);

  const handleReset = () => {
    reset({} as ISubmissionAdvanceFilters);
    onSubmit({} as ISubmissionAdvanceFilters);
    setResetFlag(true);
  };
  return (
    <FormProvider {...methods}>
      <Box
        key={resetKey}
        component="form"
        role="presentation"
        onSubmit={handleSubmit(onSubmit)}
        onReset={handleReset}
        bgcolor={WsColors.White}
        borderRadius={2}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          p={1}
          borderRadius={2}
          mb={-2}
          alignItems="center"
        >
          <WsTypography variant={WsTypoVariants.TITLE} bold={true}>
            Advanced Filters
          </WsTypography>
          <WsButton className="advance-filter" variant="contained" size="small" type="submit">
            Apply
          </WsButton>
          <IconButton type="reset">
            <RestartAltOutlinedIcon color="error" />
          </IconButton>
          <IconButton onClick={onClose}>
            <CloseIcon color="disabled" />
          </IconButton>
        </Stack>
        <List>
          <Box p={1} ml={1}>
            <WsTypography
              color={WsTypoColors.PRIMARY}
              variant={WsTypoVariants.SUB_TITLE}
              bold={true}
            >
              Default
            </WsTypography>
          </Box>
          <CreatedBwDatesFilter
            title="Submitted or Updated between"
            name="SubmittedOrUpdatedFromDate"
            name1="SubmittedOrUpdatedToDate"
            selectStatus="SubmittedOrUpdatedbetween"
            disableFuture={false}
            showAccordionIcon={true}
            isSubmittedOrUpdated={true}
          />
          <Box p={1} ml={1}>
            <WsTypography
              color={WsTypoColors.PRIMARY}
              variant={WsTypoVariants.SUB_TITLE}
              bold={true}
            >
              Client
            </WsTypography>
          </Box>
          <WsAccordion
            title="Client Name(s)"
            startIcon={<BuildingIcon />}
            titleColor={WsTypoColors.GREYTEXT}
          >
            <ClientAutocomplete name="CLIENT_NAMES" multiple={true} />
          </WsAccordion>
          <WsAccordion title="Client Recruiter" startIcon={<UserRoundIcon />}>
            <ClientRecruiterAutocomplete
              name="CLIENT_RECRUITERS"
              multiple={true}
              clients={watch('CLIENT_NAMES') ?? []}
            />
          </WsAccordion>
          <WsAccordion title={'Job Title'} startIcon={<BriefcaseBusinessIcon />}>
            <JobTitleFilterNew
              name="JOB_ID"
              placeholder="Job Title's"
              defaultValue={watch('JOB_ID')}
              freeSolo={true}
              multiple={true}
              reset={resetFlag}
            />
          </WsAccordion>
          <SalaryRangeFilter title="Salary Between" showAccordionIcon={true} />
          <LocationsFilter
            name="JOB_LOCATIONS"
            title="Job Locations"
            wisestepApi={true}
            token={tokens.token}
            authorization={tokens.auth}
            showAccordionIcon={true}
          />
          <Box p={1} ml={1}>
            <WsTypography
              color={WsTypoColors.PRIMARY}
              variant={WsTypoVariants.SUB_TITLE}
              bold={true}
            >
              Candidate
            </WsTypography>
          </Box>
          <JobTypeFilter name="JOB_TYPE" title="Candidate Job Type" showAccordionIcon={true} />
          <RelocationPreferenceFilter name="RELOCATION_PREFERENCE" showAccordionIcon={true} />
          <LocationsFilter
            name="CANDIDATE_LOCATIONS"
            title="Candidate Locations"
            wisestepApi={true}
            token={tokens.token}
            authorization={tokens.auth}
            showAccordionIcon={true}
          />
          <NoticePeriod
            name="SUBMISSION_X_HOURS_JOB_POSTING"
            title="Submitted between X hours of Job Posting"
            showAccordionIcon={true}
          />
          <CreatedBwDatesFilter
            title="Stage Date"
            name="stageDateFromDate"
            name1="stageDateToDate"
            disableFuture={false}
            showAccordionIcon={true}
          />
          <StageStatusFilter
            name="STAGE_STATUS"
            stageStatus={getValues('STAGE_STATUS')}
            showAccordionIcon={true}
          />
          <Box p={1} ml={1}>
            <WsTypography
              color={WsTypoColors.PRIMARY}
              variant={WsTypoVariants.SUB_TITLE}
              bold={true}
            >
              Team
            </WsTypography>
          </Box>
          <RecruitersFilter
            name="SUBMITTED_TEAM_MEMBER_ASSIGNED_JOB"
            title="Submitted By Team Member(s) to Assigned job"
            defaultValue={getValues('SUBMITTED_TEAM_MEMBER_ASSIGNED_JOB')}
            showAccordionIcon={true}
          />
          <TeamFilter
            name="SUBMITTED_TEAM_ASSIGNED_JOB"
            title="Submitted By Team(s) to Assigned job"
            defaultValue={
              getValues('SUBMITTED_TEAM_ASSIGNED_JOB')?.map((obj) => obj.name as string) ?? []
            }
            defaultObjects={
              getValues('SUBMITTED_TEAM_ASSIGNED_JOB') as IRecruiterTeamNameAutoCompleteOption[]
            }
            showAccordionIcon={true}
          />
          <RecruitersFilter
            name="SUBMITTED_TO_JOB_POSTED_TEAM_MEMBER"
            title="Submitted To Jobs Posted by Team Member(s)"
            defaultValue={getValues('SUBMITTED_TO_JOB_POSTED_TEAM_MEMBER')}
            showAccordionIcon={true}
          />
          <TeamFilter
            name="SUBMITTED_TO_JOB_POSTED_TEAM"
            title="Submitted To Jobs Posted by Team(s)"
            showAccordionIcon={true}
            defaultValue={
              getValues('SUBMITTED_TO_JOB_POSTED_TEAM')?.map((obj) => obj.name as string) ?? []
            }
          />
          <Box p={1} ml={1}>
            <WsTypography
              color={WsTypoColors.PRIMARY}
              variant={WsTypoVariants.SUB_TITLE}
              bold={true}
            >
              Others
            </WsTypography>
          </Box>
          <BusinessUnitFilter name="BUSINESS_UNIT" showAccordionIcon={true} />
          <TagsFilter name="TAGS" defaultValue={getValues('TAGS')} showAccordionIcon={true} />
        </List>
      </Box>
    </FormProvider>
  );
};

export default AdvancedFilters;
